"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Sparkles, Clock, ArrowUpRight, Award, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const consortia = [{
  name: "Fora",
  tier: "Preferred Partner Network",
  description: "Access to elite global hotel and experiential supplier contracts."
}, {
  name: "Virtuoso",
  tier: "Global Luxury Travel Consortium",
  description: "Top-tier status unlocking executive amenities, upgrades, and private perks."
}];
const backingPillars = [{
  title: "Institutional rate guarantees",
  description: "Direct consortium contracts ensure your family secures fully matched or privileged institutional rates across private villas, luxury lodges, and luxury hospitality.",
  icon: ShieldCheck
}, {
  title: "VIP amenities & priority upgrades",
  description: "Complimentary daily gourmet breakfast, flexible early check-in and late check-out, customized food-and-beverage credits, and priority room category upgrades upon availability.",
  icon: Sparkles
}, {
  title: "24/7 dedicated advocate care",
  description: "Round-the-clock operational backup and on-the-ground support to resolve flight adjustments, private transport nuances, and sudden schedule changes before you notice them.",
  icon: Clock
}, {
  title: "Executive supplier leverage",
  description: "General manager level relationships across global hospitality collections guarantee that multi-room family bookings receive white-glove executive attention and discreet care.",
  icon: Award
}];
export default function TrustBacking() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="trust-backing" data-nav="light" className="relative bg-background py-16 md:py-24 border-b border-border/40">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Top Section Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end pb-12 border-b border-border/60">
          <motion.div variants={itemVariants} className="lg:col-span-7">
            <span className="text-xs uppercase tracking-[0.15em] font-medium text-muted-foreground block mb-3 font-sans">
              Credibility & Consortia Backing
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-medium text-foreground tracking-tight leading-tight">
              Backed by the world’s leading travel powerhouses.
            </h2>
            <p className="mt-4 text-sm sm:text-base text-foreground/80 leading-relaxed font-sans max-w-2xl">
              As independent luxury architects under the E•Leete Group, our relationships with premiere luxury consortia provide your family milestone with institutional leverage, privileged perks, and unyielding peace of mind.
            </p>
          </motion.div>

          {/* Consortia Badges Card */}
          <motion.div variants={itemVariants} className="lg:col-span-5 flex flex-col sm:flex-row gap-4">
            {consortia.map((item, i) => <div key={item.name} data-index={i} className="flex-1 rounded-sm border border-border bg-card p-5 transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-xs">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <Badge variant="outline" className="rounded-none border-foreground/30 bg-secondary text-secondary-foreground font-sans text-xs tracking-[0.15em] uppercase px-3 py-1 font-semibold">
                    {item.name}
                  </Badge>
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                </div>
                <div className="text-xs font-semibold text-foreground tracking-wide font-sans mt-3">
                  {item.tier}
                </div>
                <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed font-sans">
                  {item.description}
                </p>
              </div>)}
          </motion.div>
        </motion.div>

        {/* 4-Pillar Grid */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} variants={containerVariants} className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {backingPillars.map((pillar, i) => {
          const IconComponent = pillar.icon;
          return <motion.div key={pillar.title} data-index={i} variants={itemVariants} className="group relative rounded-sm border border-border/60 bg-card p-6 transition-[border-color,box-shadow] duration-300 hover:border-primary/50 hover:shadow-sm flex flex-col justify-between">
                <div>
                  <div className="size-10 rounded-sm border border-border/80 bg-background flex items-center justify-center mb-5 text-foreground group-hover:text-primary transition-colors">
                    <IconComponent className="size-5 stroke-[1.25]" />
                  </div>
                  <h3 className="text-base sm:text-lg font-serif font-semibold text-foreground leading-snug">
                    {pillar.title}
                  </h3>
                  <p className="mt-2.5 text-xs sm:text-sm text-muted-foreground leading-relaxed font-sans">
                    {pillar.description}
                  </p>
                </div>

                <div className="mt-6 pt-4 border-t border-border/40 flex items-center justify-between text-xs text-muted-foreground font-medium">
                  <span>Included on all itineraries</span>
                  <span className="text-primary font-mono text-[11px]">0{i + 1}</span>
                </div>
              </motion.div>;
        })}
        </motion.div>

        {/* Bottom Micro-Bar */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={itemVariants} className="mt-12 rounded-sm bg-muted/50 border border-border/80 p-5 sm:p-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-left">
            <div className="size-2 rounded-full bg-primary shrink-0" />
            <p className="text-xs sm:text-sm text-foreground/90 font-sans">
              Planning a reunion or legacy trip? Discovery consultations review your group’s dynamic before matching suppliers.
            </p>
          </div>

          <div className="shrink-0 w-full md:w-auto">
            <Button asChild variant="outline" className="w-full md:w-auto border-foreground/30 hover:border-primary text-foreground hover:text-primary font-sans text-xs tracking-wider uppercase rounded-sm px-5 py-2 h-auto">
              <Link to="/AboutDiscoveryConsultationPage">
                <span>Start your discovery</span>
                <ArrowUpRight className="size-3.5 ml-1" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}